import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ShieldCheck, Loader2, KeyRound } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function ChangePassword() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;

  const forced = !!user.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password !== form.confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setUser(data);
      toast.success("Mot de passe modifie avec succes");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-500/20">
            <ShieldCheck className="h-5 w-5 text-teal-500" />
          </div>
          <span className="font-display text-lg font-extrabold text-slate-900">Scolaris <span className="text-teal-600">Central</span></span>
        </div>
        <h2 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-teal-500" /> Changer le mot de passe
        </h2>
        {forced && (
          <p data-testid="force-change-notice" className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Pour des raisons de securite, vous devez definir un nouveau mot de passe avant d'acceder a l'application.
          </p>
        )}
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="cur">Mot de passe actuel</Label>
            <Input id="cur" data-testid="current-password" type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} required className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="np">Nouveau mot de passe</Label>
            <Input id="np" data-testid="new-password" type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} required minLength={12} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="cf">Confirmer le mot de passe</Label>
            <Input id="cf" data-testid="confirm-password" type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required className="mt-1.5" />
          </div>
          {error && <p data-testid="change-error" className="text-sm text-rose-600">{error}</p>}
          <Button type="submit" data-testid="change-submit" disabled={loading} className="w-full bg-teal-600 hover:bg-teal-700">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer le nouveau mot de passe"}
          </Button>
        </form>
        <button onClick={doLogout} className="mt-5 text-sm text-slate-500 hover:text-teal-600">Se deconnecter</button>
      </div>
    </div>
  );
}
